/** https://open.pinduoduo.com/application/document/browse?idStr=263992F97736A864  */
import delay from "@/utils/delay";
import to from "@/utils/to";
import { observable, makeObservable, actionBound } from "mobx";
import PddDeviceFingerPrintError, { PddDeviceFingerPrintErrorType } from "./PddDeviceFingerPrintError";
import { getPageCodeRequestUrl, getAxios } from "./util";
import { LoggerInfoTag, postLogInfo } from "@/services/api/log";
import ShopType from "@/enum/ShopType";
import { isEmpty } from "@/utils";

/** 运行下一次获取Pati的时间间隔 */
const RUN_NEXT_PATI_ROUND_TIME = 1000 * 60 * 55;
/** 运行下一次获取PageCode的时间间隔 */
const RUN_NEXT_PAGE_CODE_ROUND_TIME = 1000 * 60 * 60 * 11;
/** 任务失败重试时间 */
const TASK_RETRY_TIME = 1000 * 3;

const XPddPageCodeCachePrefixKey = "__XPddPageCodeCache__";
const XPddPatiCachePrefixKey = "__XPddPatiCache__";

/** 获取Pati的超时时间 */
const EXEC_GET_PATI_TIMEOUT = 10 * 1000;
/** 执行PDD_OPEN_init的最大等待时间 */
const EXEC_PDD_OPEN_INIT_TIMEOUT = 1000;

class PddDeviceFingerPrintFetcher {
    constructor(public globalStore) {
        makeObservable(this, {
            loading: observable,
            buildUserInfo: actionBound,
            handleError: actionBound,
            startPddDeviceFingerPrintFetcherTask: actionBound,
            cancelTask: actionBound,
            getPageCode: actionBound,
            pddOpenInit: actionBound,
            setPati: actionBound,
            doGetPddPati: actionBound,
            getPddPati: actionBound,
        });
        this.currentUserShopId = globalStore?.currentUser?.shopId;

        const xPddPatiCache = localStorage.getItem(XPddPatiCachePrefixKey + this.currentUserShopId);
        const xPddPageCodeCache = localStorage.getItem(XPddPageCodeCachePrefixKey + this.currentUserShopId);

        if (xPddPatiCache) {
            this.xPddPati = xPddPatiCache;
        }

        if (xPddPageCodeCache) {
            this.xPddPageCode = xPddPageCodeCache;
        }
    }

    isInitializedSDK = false;

    currentUserShopId = null;

    /** 是否为第一次执行任务 */
    firstExec = true;

    /** 会阻塞拼多多节点的HTTP请求 */
    loading = false;

    /** -------------------------- */

    /** 从平台获取到的pati */
    xPddPati = null;

    /** 从发货易获取到的code */
    xPddPageCode = null;

    taskTimer: NodeJS.Timeout;

    /**
     * 发货易Token是否失效
     * 失效则取消任务
     */
    tokenExpired = false;

    buildUserInfo() {
        const userInfo = `【username:${this.globalStore.currentUser.username}】`;
        const shopInfo = `【currentUserShopId:${this.globalStore.currentUser.shopId}】`;
        const associatedShopInfo = `【pddAllowedShopIds:${this.globalStore.allowedShops
            .filter(({ type }) =>
                [
                    ShopType.PINDUODUO,
                    ShopType.PDD_FDS,
                    ShopType.PDD_DROP_SHIPPING_SUPPLIER,
                    ShopType.PDD_FULFILLMENT,
                ].includes(type),
            )
            .map(({ id }) => id)
            .join()}】`;

        const pageCodeInfo = `【pageCode:${this.xPddPageCode || ""}】`;
        const patiInfo = `【pati:${this.xPddPati || ""}】`;
        const browserInfo = `【browser:${navigator?.userAgent}】`;
        const cookieInfo = `【cookie:${document.cookie}】`;

        return userInfo + shopInfo + associatedShopInfo + pageCodeInfo + patiInfo + browserInfo + cookieInfo;
    }

    // 处理每个阶段的错误
    handleError = (ecTokenExpiredError, contextType: PddDeviceFingerPrintErrorType, errorMessage: string = "") => {
        if (ecTokenExpiredError?.response?.status === 401 || ecTokenExpiredError?.response?.status === 403) {
            this.tokenExpired = true;
            this.cancelTask();

            return null;
        }

        const pddDeviceFingerPrintError = new PddDeviceFingerPrintError(contextType, errorMessage);

        const content = this.buildUserInfo() + pddDeviceFingerPrintError?.message;

        postLogInfo(
            {
                tag: LoggerInfoTag.PDD_DEVICE_FINGER,
                content,
            },
            {
                hideModal: true,
            },
        );

        console.error(content);

        return null;
    };

    async startPddDeviceFingerPrintFetcherTask(forceGetPati = false, forceGetPageCode = false) {
        if (this.tokenExpired) return;

        try {
            if (this.firstExec) {
                this.loading = true;
            }

            let pageCodeResult = forceGetPageCode ? null : this.xPddPageCode;
            if (forceGetPageCode) {
                pageCodeResult = await this.getPageCode();
            }

            await this.pddOpenInit();

            let patiResult = forceGetPati ? null : this.xPddPati;
            if (forceGetPati) {
                patiResult = await this.getPddPati();
            }

            this.cancelTask();

            if (this.firstExec) {
                this.loading = false;
                this.firstExec = false;
            }

            if (!this.tokenExpired) {
                // 缺少任一值，等待N秒重新开启任务
                if (!patiResult || isEmpty(pageCodeResult)) {
                    this.taskTimer = setTimeout(() => {
                        this.startPddDeviceFingerPrintFetcherTask(!patiResult, isEmpty(pageCodeResult));
                    }, TASK_RETRY_TIME);
                } else {
                    // 成功获取到所有的信息，等待下一次任务的执行
                    console.log(`xPddPati:${this.xPddPati}`);
                    console.log(`xPddPageCode:${this.xPddPageCode}`);

                    /** 55分钟后重新开始获取Pati*/
                    setTimeout(() => {
                        console.log("开始执行下一轮获取Pati任务");
                        this.startPddDeviceFingerPrintFetcherTask(true, false);
                    }, RUN_NEXT_PATI_ROUND_TIME);

                    /** 11小时后重新获取PageCode */
                    setTimeout(() => {
                        console.log("开始执行下一轮获取PageCode任务");
                        this.startPddDeviceFingerPrintFetcherTask(false, true);
                    }, RUN_NEXT_PAGE_CODE_ROUND_TIME);
                }
            }
        } catch (e) {
            // 这里是不会有异常
            console.error(e);
        } finally {
            this.loading = false;
            this.firstExec = false;
        }
    }

    cancelTask() {
        if (this.taskTimer) {
            clearTimeout(this.taskTimer);
            this.taskTimer = null;
        }
    }

    async getPageCode() {
        const requestUrl = await getPageCodeRequestUrl(this.globalStore.allowedShops);
        const axios = getAxios();

        // 空字符串表示拼多多供货商无拼多多代理商
        const [error, result] = await to<string, any>(axios.get(requestUrl).then((res) => res.data.data));

        if (error) {
            return this.handleError(error, PddDeviceFingerPrintErrorType.GET_PAGE_CODE_ERROR, error?.message);
        }

        this.xPddPageCode = result;
        localStorage.setItem(XPddPageCodeCachePrefixKey + this.currentUserShopId, result);

        return result;
    }

    async pddOpenInit() {
        if (!window.PDD_OPEN_init) {
            this.handleError(null, PddDeviceFingerPrintErrorType.NO_PDD_OPEN_INIT_ERROR_FUNC);

            return;
        }

        if (this.isInitializedSDK) return;

        try {
            // ❗️window.PDD_OPEN_init是要在window.onload后执行，如果页面还没加载完成（比如一些async脚本还没加载进来），这里会等到加载进来再执行，所以添加一个超时的操作
            await Promise.race([window.PDD_OPEN_init({ code: this.xPddPageCode }), delay(EXEC_PDD_OPEN_INIT_TIMEOUT)]);

            this.isInitializedSDK = true;
        } catch (error) {
            this.handleError(null, PddDeviceFingerPrintErrorType.PDD_OPEN_INIT_EXEC_ERROR, error?.message);
        } finally {
            await delay(1500);
        }
    }

    setPati(pati: string) {
        if (pati) {
            this.xPddPati = pati;
            localStorage.setItem(XPddPatiCachePrefixKey + this.currentUserShopId, pati);
        }
    }

    async doGetPddPati() {
        const racePati = async () => {
            // 添加超时机制，避免阻塞时间过长
            return Promise.race([
                window.PDD_OPEN_getPati().then((data) => {
                    //这里作用是超时了，也继续写入pati
                    this.setPati(data);

                    return data;
                }),
                new Promise<string>((_, reject) => {
                    setTimeout(() => {
                        reject(new PddDeviceFingerPrintError(PddDeviceFingerPrintErrorType.GET_PDD_PATI_TIMEOUT, ""));
                    }, EXEC_GET_PATI_TIMEOUT);
                }),
            ]);
        };

        return racePati();

        /** 如果后续指标还不通过，则开启下面的暴力代码 */

        // if (this.xPddPati) return racePati();

        /**
         *  由于在firefox浏览器中，在发货易cookie清空的情况下，前两次获取pati一定是空的（像是PDD的bug），
         *  所以这里添加重试机制，如果pati没有缓存值，则需要尝试3次
         */
        // let time = 1;
        // for (; time <= 3; time++) {
        //     const res = await racePati();

        //     console.log(`第${time}次pati结果:${res}`);

        //     if (res) return res;
        // }

        // throw new Error(`3次从Pati获取到空内容`);
    }

    async getPddPati() {
        if (!window.PDD_OPEN_getPati) {
            this.handleError(null, PddDeviceFingerPrintErrorType.NO_PDD_OPEN_GET_PATI_FUNC);

            return;
        }

        const [error, xPddPati] = await to(this.doGetPddPati());

        if (error) {
            return this.handleError(null, PddDeviceFingerPrintErrorType.GET_PDD_PATI_ERROR, error?.message);
        } else if (!xPddPati) {
            return this.handleError(null, PddDeviceFingerPrintErrorType.PATI_NULL_ERROR, "");
        }

        this.setPati(xPddPati);

        return xPddPati;
    }
}

let instance: PddDeviceFingerPrintFetcher;

export function buildPddDeviceFingerPrintFetcher(globalStore) {
    if (instance) return instance;

    instance = new PddDeviceFingerPrintFetcher(globalStore);

    return instance;
}

export function getPddDeviceFingerPrinterFetcherInstance() {
    return instance;
}
